import { type ReactNode } from "react";
import { Link } from "react-router-dom";

import { useRevokeShare, useShares } from "../../api/sharing";
import { formatRelativeTime } from "../../lib/time";
import { Button } from "../../ui/Button";
import { useToast } from "../../ui/toast-context";

/**
 * Settings → Shared links (`docs/spec/shares.md` §4.3). A member sees the links
 * they created; a workspace administrator sees every link in the workspace, and
 * only an administrator is told when each was last opened.
 */
export function SharesScreen(): ReactNode {
  const shares = useShares();
  const revoke = useRevokeShare();
  const toast = useToast();

  return (
    <section className="max-w-3xl">
      <h2 className="text-xl font-semibold">Shared links</h2>
      <p className="mt-1 mb-5 text-sm text-muted">
        Public links to documents and collections, with their traffic.
      </p>

      {shares.isPending && (
        <p role="status" className="text-sm text-muted">
          Loading shared links…
        </p>
      )}
      {shares.isError && (
        <p role="alert" className="text-sm text-danger">
          {shares.error.message}
        </p>
      )}
      {shares.data?.length === 0 && (
        <p className="text-sm text-muted">Nothing has been shared publicly yet.</p>
      )}

      {shares.data !== undefined && shares.data.length > 0 && (
        <table className="w-full text-left text-sm">
          <caption className="sr-only">Shared links</caption>
          <thead className="text-xs text-muted">
            <tr>
              <th scope="col" className="py-2">
                Document
              </th>
              <th scope="col">Link</th>
              <th scope="col">Views</th>
              <th scope="col">Last opened</th>
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shares.data.map((share) => (
              <tr key={share.id} className="border-t border-line">
                <td className="py-2">
                  {share.sourcePath === null ? (
                    (share.sourceTitle ?? "Untitled")
                  ) : (
                    <Link to={share.sourcePath} className="text-accent underline">
                      {share.sourceTitle ?? "Untitled"}
                    </Link>
                  )}
                  {!share.published && <span className="ml-2 text-xs text-muted">Unpublished</span>}
                </td>
                <td className="max-w-48 truncate font-mono text-xs">{share.url}</td>
                <td>{share.views}</td>
                <td className="text-xs text-muted">
                  {share.lastAccessedAt == null ? "—" : formatRelativeTime(share.lastAccessedAt)}
                </td>
                <td className="text-right">
                  <Button
                    size="sm"
                    aria-label={`Revoke the link to ${share.sourceTitle ?? "this document"}`}
                    disabled={revoke.isPending}
                    onClick={() => {
                      revoke.mutate(
                        {
                          id: share.id,
                          ...(share.documentId === null ? {} : { documentId: share.documentId }),
                          ...(share.collectionId === null
                            ? {}
                            : { collectionId: share.collectionId }),
                        },
                        {
                          onSuccess: () => {
                            toast.show("Link revoked");
                          },
                          onError: (error) => {
                            toast.show(error.message, "error");
                          },
                        },
                      );
                    }}
                  >
                    Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
